import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  getDoorProductSettings,
  isDoorProductEnabled,
  type IDoorProductSettings,
} from '@/api/productSettings';
import { getAccountIdFromUrlOrDom } from '@/utils/account';

interface IAccountSettings {
  juid?: string;
  a5Account?: number[] | string;
  [key: string]: unknown;
}

interface IProductSettings {
  [key: string]: unknown;
}

export const useAccountStore = defineStore('account', () => {
  const isClientUser = ref(false);
  const sessionToken = ref('');
  const inventoryIds = ref<number[]>([]);
  const accountSettings = ref<IAccountSettings | null>(null);
  const productSettings = ref<IProductSettings | null>(null);

  // CX product enable gate
  const accountId = ref(getAccountIdFromUrlOrDom());
  const productLoading = ref(false);
  const productChecked = ref(false);
  const productEnabled = ref(false);
  const productError = ref<string | null>(null);
  const doorProductSettings = ref<IDoorProductSettings | null>(null);

  const currentAccountId = computed(() => {
    return accountId.value || inventoryIds.value[0]?.toString() || '';
  });

  const setAccountSettings = (settings: IAccountSettings | null) => {
    const rawIds = settings?.a5Account ?? [];
    const ensuredArrayOfIds = Array.isArray(rawIds)
      ? rawIds
      : String(rawIds)
          .split(',')
          .map((id: string) => id.trim());
    const ensuredNumberIds = ensuredArrayOfIds.map((id: string | number) => Number(id));
    setInventoryId(ensuredNumberIds);

    accountSettings.value = settings;
  };

  const setIsClientUser = (value: boolean) => {
    isClientUser.value = value;
  };

  const setSessionToken = (token: string) => {
    sessionToken.value = token;
  };

  const getAccountSettings = (): IAccountSettings | null => {
    return accountSettings.value;
  };

  const setInventoryId = (newInventoryIds: number[]) => {
    inventoryIds.value = newInventoryIds;
  };

  const setProductSettings = (newProductSettings: IProductSettings | null) => {
    productSettings.value = newProductSettings;
  };

  const getProductSettings = (): IProductSettings | null => {
    return productSettings.value;
  };

  const checkProductEnabled = async (routeAccountId?: unknown) => {
    if (productChecked.value) return;

    productLoading.value = true;
    productError.value = null;

    try {
      const id = getAccountIdFromUrlOrDom(routeAccountId) || accountId.value;
      accountId.value = id;

      if (!id) {
        productEnabled.value = false;
        productError.value =
          'This page isn’t available right now. Please contact the dealership for help.';
        return;
      }

      const settings = await getDoorProductSettings(id);
      doorProductSettings.value = settings;
      productSettings.value = settings;

      if (!isDoorProductEnabled(settings)) {
        productEnabled.value = false;
        productError.value =
          'Vehicle ordering isn’t available for this dealership right now. Please check back later or contact the dealership for assistance.';
        return;
      }

      productEnabled.value = true;
      productError.value = null;
    } finally {
      productChecked.value = true;
      productLoading.value = false;
    }
  };

  return {
    currentAccountId,
    accountId,
    isClientUser,
    sessionToken,
    inventoryIds,
    accountSettings,
    productSettings,
    productLoading,
    productChecked,
    productEnabled,
    productError,
    doorProductSettings,
    setAccountSettings,
    setIsClientUser,
    setSessionToken,
    getAccountSettings,
    setInventoryId,
    setProductSettings,
    getProductSettings,
    checkProductEnabled,
  };
});
